/**
 * Guided Setup — public 6-step onboarding questionnaire.
 *
 * Section 1: Business basics (business_category, avg_bill_value, primary_goal)
 * Section 2: Unconditional coupons
 * Section 3: Conditional coupons
 * Section 4: Coupon behavior
 * Section 5: Redemption
 * Section 6: Create Your Account — reuses the exact same VendorRegister
 *            fields as /vendor/register. Business type is pre-filled
 *            from Section 1's answer.
 *
 * The final POST hits /auth/vendor/register-guided which internally
 * calls the SAME vendor-creation helper as /register, so trial issuance
 * and verification-email flows are identical between the two paths.
 */
import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { PasswordInput } from "@/components/ui/password-input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Loader2, ArrowRight, ArrowLeft, Sparkles, CheckCircle2,
} from "lucide-react";
import Logo from "@/components/Logo";
import { Toaster } from "@/components/ui/sonner";
import { Doodle } from "@/components/marketing/Decor";
import { BUSINESS_TYPES } from "@/lib/businessTypes";
import { toast } from "sonner";

const STEPS = [
  { key: "basics",      label: "Business basics",     testid: "gs-step-1" },
  { key: "free_reward", label: "Free-spin reward",    testid: "gs-step-2" },
  { key: "bonus",       label: "Bigger reward",       testid: "gs-step-3" },
  { key: "behavior",    label: "Coupon behavior",     testid: "gs-step-4" },
  { key: "redemption",  label: "Redemption",          testid: "gs-step-5" },
  { key: "account",     label: "Create account",      testid: "gs-step-6" },
];

const REWARD_TYPE_OPTIONS = [
  { value: "percent",    label: "% off (e.g. 10% off bill)" },
  { value: "flat",       label: "Flat ₹ amount off (e.g. ₹50 off)" },
  { value: "free_addon", label: "Free add-on (e.g. free tea, free dessert)" },
];

const PRIMARY_GOAL_OPTIONS = [
  { value: "new_customers",     label: "Attract new customers" },
  { value: "repeat_customers",  label: "Bring existing customers back more often" },
  { value: "higher_bill_value", label: "Grow the size of each bill" },
];

const VALIDATION_METHOD_OPTIONS = [
  { value: "staff", label: "My staff will check coupon codes manually" },
  { value: "scan",  label: "I want scan-based validation" },
];

const emptyForm = {
  // Section 1
  business_category: "Cafe",
  avg_bill_value: "",
  primary_goal: "",
  // Section 2
  min_free_reward: "",
  existing_walkin_offers: "",
  max_monthly_budget: "",
  preferred_reward_type: "",
  // Section 3
  min_bill_for_bonus: "",
  slow_moving_products: "",
  wants_repeat_visit_reward: null,
  repeat_visit_detail: "",
  wants_reuse_time_gap: null,
  reuse_time_gap_days: "",
  // Section 4
  coupon_expiry_days: "",
  wants_redemption_cap: null,
  redemption_cap_detail: "",
  wants_day_of_week_variation: null,
  wants_new_vs_returning_split: null,
  // Section 5
  validation_method: "",
  wants_sms_whatsapp_confirmation: null,
  // Registration
  name: "", business: "", business_type_other: "", city: "",
  phone: "", email: "", password: "", gst_number: "", agree: false,
};

export default function GuidedSetupPage() {
  const { registerGuided } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(emptyForm);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  // ── Per-step validity ──────────────────────────────────────────────
  const stepValid = useMemo(() => {
    switch (step) {
      case 0:
        return !!form.business_category && !!form.primary_goal;
      case 1:
      case 2:
      case 3:
      case 4:
        // Sections 2-5 are entirely optional (spec explicitly wants us
        // to allow low-friction skipping — every field on these pages
        // is Optional[…] in the Pydantic schema). Advance is always
        // allowed once the vendor has actually reached the step.
        return true;
      case 5: {
        const phoneOk = /^\+?[0-9 ()-]{10,}$/.test(form.phone);
        const otherOk =
          form.business_category !== "Other" ||
          form.business_type_other.trim().length > 0;
        return (
          !!form.name.trim() &&
          !!form.business.trim() &&
          otherOk &&
          !!form.city.trim() &&
          phoneOk &&
          /\S+@\S+\.\S+/.test(form.email) &&
          form.password.length >= 8 &&
          form.agree
        );
      }
      default:
        return false;
    }
  }, [step, form]);

  const goNext = () => {
    setErr("");
    if (!stepValid) {
      setErr("Please answer the required fields on this step.");
      return;
    }
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const goPrev = () => {
    setErr("");
    setStep((s) => Math.max(s - 1, 0));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // ── Submit ──────────────────────────────────────────────────────────
  const submit = async () => {
    if (!stepValid) {
      setErr("Please complete all required fields to activate your trial.");
      return;
    }
    setErr("");
    setBusy(true);
    try {
      // Registration payload — mirrors VendorRegister exactly. Business
      // type is pulled from Section 1's answer, `business_type_other`
      // only travels when the vendor picked "Other".
      const registration = {
        name: form.name.trim(),
        business: form.business.trim(),
        business_type: form.business_category,
        business_type_other:
          form.business_category === "Other"
            ? form.business_type_other.trim()
            : null,
        city: form.city.trim(),
        phone: form.phone.trim(),
        email: form.email.trim().toLowerCase(),
        password: form.password,
        gst_number: form.gst_number.trim() || null,
      };
      // Intake payload — VendorIntakeResponse shape. Empty strings/nulls
      // are fine; the backend treats them as unfilled signals.
      const intake = {
        business_category: form.business_category,
        avg_bill_value: form.avg_bill_value || null,
        primary_goal: form.primary_goal,
        min_free_reward: form.min_free_reward || null,
        existing_walkin_offers: form.existing_walkin_offers || null,
        max_monthly_budget: form.max_monthly_budget || null,
        preferred_reward_type: form.preferred_reward_type || null,
        min_bill_for_bonus: form.min_bill_for_bonus || null,
        slow_moving_products: form.slow_moving_products || null,
        wants_repeat_visit_reward: form.wants_repeat_visit_reward,
        repeat_visit_detail: form.repeat_visit_detail || null,
        wants_reuse_time_gap: form.wants_reuse_time_gap,
        reuse_time_gap_days: form.reuse_time_gap_days
          ? Number(form.reuse_time_gap_days)
          : null,
        coupon_expiry_days: form.coupon_expiry_days
          ? Number(form.coupon_expiry_days)
          : null,
        wants_redemption_cap: form.wants_redemption_cap,
        redemption_cap_detail: form.redemption_cap_detail || null,
        wants_day_of_week_variation: form.wants_day_of_week_variation,
        wants_new_vs_returning_split: form.wants_new_vs_returning_split,
        validation_method: form.validation_method || null,
        wants_sms_whatsapp_confirmation: form.wants_sms_whatsapp_confirmation,
      };
      await registerGuided({ registration, intake });
      toast.success("Your 15-day free trial is now active. Welcome to CityDooh!");
      navigate("/vendor/dashboard", { replace: true });
    } catch (e) {
      const msg = e?.response?.data?.detail || "Something went wrong. Please try again.";
      setErr(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="app-light mkt-root mkt-paper-noise min-h-screen px-4 py-10 relative overflow-hidden">
      <Toaster richColors position="top-center" />
      <Doodle.Crown className="hidden md:block absolute left-8 top-6 h-12 w-16 text-[var(--graffiti-yellow-deep)] -rotate-6" />
      <Doodle.Star className="hidden md:block absolute right-12 top-12 h-10 w-10 text-black/80" />
      <Doodle.Heart className="hidden md:block absolute right-8 bottom-14 h-10 w-10 text-[var(--graffiti-yellow-deep)] rotate-12" />

      <div className="max-w-2xl mx-auto relative" data-testid="guided-setup-page">
        <div className="flex justify-center mb-6">
          <Link to="/" aria-label="City DOOH home" className="inline-flex focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-black rounded-md">
            <Logo size={130} light />
          </Link>
        </div>

        {/* ── Header + progress ─────────────────────────────────────── */}
        <div className="mkt-sticker p-6 md:p-8 relative">
          <span className="mkt-tape absolute -top-4 left-6">Guided Setup</span>
          <h1
            className="font-display uppercase text-3xl md:text-4xl mt-3 leading-none"
            data-testid="guided-setup-title"
          >
            Let's design your <span className="mkt-highlight">spin campaign</span>
          </h1>
          <p className="text-sm text-black/70 mt-2">
            {STEPS.length - 1} quick questions, then we activate your{" "}
            <strong>15-day free trial</strong> instantly. Nothing here is set
            in stone — you can change every setting later inside your dashboard.
          </p>

          {/* Progress dots */}
          <div className="flex items-center gap-2 mt-5" aria-label="Setup progress">
            {STEPS.map((s, i) => (
              <div
                key={s.key}
                className={`h-2 flex-1 rounded-full border-2 border-black transition-colors ${
                  i < step
                    ? "bg-[var(--graffiti-yellow-deep)]"
                    : i === step
                    ? "bg-black"
                    : "bg-white"
                }`}
                data-testid={`gs-progress-${i}`}
                aria-current={i === step ? "step" : undefined}
              />
            ))}
          </div>
          <p className="text-xs text-black/60 mt-2 uppercase tracking-wider font-bold">
            Step {step + 1} of {STEPS.length} — {STEPS[step].label}
          </p>
        </div>

        {/* ── Step body ─────────────────────────────────────────────── */}
        <div className="mkt-sticker p-6 md:p-8 mt-6" data-testid={STEPS[step].testid}>
          {step === 0 && <StepBasics form={form} update={update} />}
          {step === 1 && <StepFreeReward form={form} update={update} />}
          {step === 2 && <StepBonus form={form} update={update} />}
          {step === 3 && <StepBehavior form={form} update={update} />}
          {step === 4 && <StepRedemption form={form} update={update} />}
          {step === 5 && <StepAccount form={form} update={update} />}

          {err && (
            <p
              className="mt-5 text-sm font-bold text-[#8b1212] bg-[#fbd5d5] border-2 border-[#8b1212] rounded-md px-3 py-2"
              data-testid="gs-error"
            >
              {err}
            </p>
          )}

          {/* Nav row */}
          <div className="flex items-center justify-between gap-3 mt-7">
            <button
              type="button"
              onClick={goPrev}
              disabled={step === 0 || busy}
              className="mkt-btn mkt-btn--ghost disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="gs-back"
            >
              <ArrowLeft className="h-4 w-4" /> Back
            </button>

            {step < STEPS.length - 1 ? (
              <button
                type="button"
                onClick={goNext}
                disabled={busy}
                className="mkt-btn"
                data-testid="gs-next"
              >
                Next <ArrowRight className="h-4 w-4" />
              </button>
            ) : (
              <button
                type="button"
                onClick={submit}
                disabled={busy || !stepValid}
                className="mkt-btn mkt-btn--dark disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid="gs-submit"
              >
                {busy ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <Sparkles className="h-4 w-4" />
                    Activate my 15-day free trial
                  </>
                )}
              </button>
            )}
          </div>

          <p className="text-xs text-black/55 mt-4 text-center">
            Already have an account?{" "}
            <Link
              to="/vendor/login"
              className="font-bold text-black underline decoration-[var(--graffiti-yellow)] decoration-4 underline-offset-2"
            >
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

/* ─── Reusable field primitives ──────────────────────────────────── */

const Field = ({ label, hint, children, required }) => (
  <div className="space-y-1.5">
    <Label className="uppercase text-[11px] font-bold tracking-wider text-black">
      {label}
      {required && <span className="text-[#8b1212] ml-1">*</span>}
    </Label>
    {children}
    {hint && <p className="text-[11px] text-black/60 leading-snug">{hint}</p>}
  </div>
);

const YesNo = ({ value, onChange, testid }) => (
  <div className="flex gap-2" data-testid={testid}>
    {[
      { v: true,  label: "Yes" },
      { v: false, label: "No" },
    ].map((opt) => {
      const selected = value === opt.v;
      return (
        <button
          key={String(opt.v)}
          type="button"
          onClick={() => onChange(opt.v)}
          className={`px-4 h-10 rounded-md border-2 border-black font-bold uppercase text-xs tracking-wider transition-transform ${
            selected
              ? "bg-black text-[var(--graffiti-yellow-deep)] shadow-[3px_3px_0_var(--graffiti-yellow-deep)]"
              : "bg-white text-black hover:-translate-y-0.5"
          }`}
          data-testid={`${testid}-${opt.v ? "yes" : "no"}`}
          aria-pressed={selected}
        >
          {selected && <CheckCircle2 className="inline h-3.5 w-3.5 mr-1" />}
          {opt.label}
        </button>
      );
    })}
  </div>
);

/* ─── Step 1 — Business basics ───────────────────────────────────── */

const StepBasics = ({ form, update }) => (
  <div className="space-y-5">
    <SectionIntro
      title="Tell us about your business"
      body="Two questions to shape the rest of the setup."
    />
    <Field label="Business category" required>
      <Select
        value={form.business_category}
        onValueChange={(v) => update("business_category", v)}
      >
        <SelectTrigger
          data-testid="gs-business-category"
          className="h-11 border-2 border-black bg-white"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {BUSINESS_TYPES.map((t) => (
            <SelectItem key={t} value={t}>{t}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </Field>

    <Field
      label="Average bill / order value"
      hint="Roughly, in ₹ — used to suggest sensible reward amounts."
    >
      <Input
        value={form.avg_bill_value}
        onChange={(e) => update("avg_bill_value", e.target.value)}
        placeholder="e.g. ₹300, ₹1500"
        data-testid="gs-avg-bill-value"
        className="h-11 border-2 border-black bg-white"
      />
    </Field>

    <Field
      label="What is your primary goal?"
      required
      hint="Pick the ONE that matters most right now."
    >
      <RadioGroup
        value={form.primary_goal}
        onValueChange={(v) => update("primary_goal", v)}
        className="space-y-2"
      >
        {PRIMARY_GOAL_OPTIONS.map((opt) => (
          <label
            key={opt.value}
            htmlFor={`goal-${opt.value}`}
            className={`flex items-start gap-3 px-3 py-2.5 rounded-md border-2 cursor-pointer transition-transform hover:-translate-y-0.5 ${
              form.primary_goal === opt.value
                ? "border-black bg-[var(--graffiti-yellow)] shadow-[3px_3px_0_black]"
                : "border-black bg-white"
            }`}
          >
            <RadioGroupItem
              id={`goal-${opt.value}`}
              value={opt.value}
              data-testid={`gs-goal-${opt.value}`}
              className="mt-0.5 border-black"
            />
            <span className="text-sm font-bold text-black leading-snug">
              {opt.label}
            </span>
          </label>
        ))}
      </RadioGroup>
    </Field>
  </div>
);

/* ─── Step 2 — Unconditional coupons ─────────────────────────────── */

const StepFreeReward = ({ form, update }) => (
  <div className="space-y-5">
    <SectionIntro
      title="Free-spin (unconditional) rewards"
      body="Every spinner wins something small — a low-cost, no-strings reward that gets them in the door."
    />
    <Field
      label="Smallest comfortable free-spin reward"
      hint="Something you'd happily give to every walk-in."
    >
      <Input
        value={form.min_free_reward}
        onChange={(e) => update("min_free_reward", e.target.value)}
        placeholder="e.g. 5% off, free tea, ₹20 off"
        data-testid="gs-min-free-reward"
        className="h-11 border-2 border-black bg-white"
      />
    </Field>

    <Field
      label="Existing walk-in offers you already run?"
      hint="Great — we can port them onto the wheel."
    >
      <Textarea
        value={form.existing_walkin_offers}
        onChange={(e) => update("existing_walkin_offers", e.target.value)}
        placeholder="e.g. Buy 1 get 1 on Tuesdays, 10% happy hours"
        rows={2}
        data-testid="gs-existing-walkin-offers"
        className="border-2 border-black bg-white"
      />
    </Field>

    <Field
      label="Max monthly spend on free rewards"
      hint="At ~100 spins/month, roughly how much are you comfortable giving away?"
    >
      <Input
        value={form.max_monthly_budget}
        onChange={(e) => update("max_monthly_budget", e.target.value)}
        placeholder="e.g. ₹5,000 / month"
        data-testid="gs-max-monthly-budget"
        className="h-11 border-2 border-black bg-white"
      />
    </Field>

    <Field label="Preferred reward format">
      <RadioGroup
        value={form.preferred_reward_type}
        onValueChange={(v) => update("preferred_reward_type", v)}
        className="grid grid-cols-1 gap-2"
      >
        {REWARD_TYPE_OPTIONS.map((opt) => (
          <label
            key={opt.value}
            htmlFor={`rt-${opt.value}`}
            className={`flex items-start gap-3 px-3 py-2.5 rounded-md border-2 cursor-pointer transition-transform hover:-translate-y-0.5 ${
              form.preferred_reward_type === opt.value
                ? "border-black bg-[var(--graffiti-yellow)] shadow-[3px_3px_0_black]"
                : "border-black bg-white"
            }`}
          >
            <RadioGroupItem
              id={`rt-${opt.value}`}
              value={opt.value}
              data-testid={`gs-rewardtype-${opt.value}`}
              className="mt-0.5 border-black"
            />
            <span className="text-sm font-bold text-black leading-snug">
              {opt.label}
            </span>
          </label>
        ))}
      </RadioGroup>
    </Field>
  </div>
);

/* ─── Step 3 — Conditional coupons ───────────────────────────────── */

const StepBonus = ({ form, update }) => (
  <div className="space-y-5">
    <SectionIntro
      title="Bigger, conditional rewards"
      body="Bigger prizes on the wheel that only unlock when a customer meets a condition (min bill, repeat visit, etc). This is the lever that grows bill value."
    />

    <Field
      label="Ideal minimum bill for a bigger reward"
      hint="Set a floor so bigger prizes stay profitable."
    >
      <Input
        value={form.min_bill_for_bonus}
        onChange={(e) => update("min_bill_for_bonus", e.target.value)}
        placeholder="e.g. ₹500, ₹1,200"
        data-testid="gs-min-bill-for-bonus"
        className="h-11 border-2 border-black bg-white"
      />
    </Field>

    <Field
      label="Slow-moving products you'd like to push?"
      hint="We'll suggest bundling these into bigger wheel prizes."
    >
      <Textarea
        value={form.slow_moving_products}
        onChange={(e) => update("slow_moving_products", e.target.value)}
        placeholder="e.g. Iced Americano, Winter jackets, Cold-press juices"
        rows={2}
        data-testid="gs-slow-moving-products"
        className="border-2 border-black bg-white"
      />
    </Field>

    <Field label="Reward repeat visits with something better?">
      <YesNo
        value={form.wants_repeat_visit_reward}
        onChange={(v) => update("wants_repeat_visit_reward", v)}
        testid="gs-wants-repeat"
      />
      {form.wants_repeat_visit_reward === true && (
        <Textarea
          value={form.repeat_visit_detail}
          onChange={(e) => update("repeat_visit_detail", e.target.value)}
          placeholder="e.g. 20% off on 3rd visit"
          rows={2}
          data-testid="gs-repeat-detail"
          className="mt-2 border-2 border-black bg-white"
        />
      )}
    </Field>

    <Field label="Enforce a minimum time gap between reuses?">
      <YesNo
        value={form.wants_reuse_time_gap}
        onChange={(v) => update("wants_reuse_time_gap", v)}
        testid="gs-wants-gap"
      />
      {form.wants_reuse_time_gap === true && (
        <div className="mt-2 flex items-center gap-2">
          <Input
            type="number"
            min={1}
            max={365}
            value={form.reuse_time_gap_days}
            onChange={(e) => update("reuse_time_gap_days", e.target.value)}
            placeholder="e.g. 7"
            data-testid="gs-gap-days"
            className="h-11 border-2 border-black bg-white w-32"
          />
          <span className="text-sm font-bold text-black">days</span>
        </div>
      )}
    </Field>
  </div>
);

/* ─── Step 4 — Coupon behavior ───────────────────────────────────── */

const StepBehavior = ({ form, update }) => (
  <div className="space-y-5">
    <SectionIntro
      title="How should coupons behave?"
      body="Fine controls — sensible defaults if you skip these."
    />

    <Field
      label="Coupon expiry (days)"
      hint="Most SMBs land between 3 and 7 days for maximum urgency."
    >
      <Input
        type="number"
        min={1}
        max={365}
        value={form.coupon_expiry_days}
        onChange={(e) => update("coupon_expiry_days", e.target.value)}
        placeholder="e.g. 5"
        data-testid="gs-expiry-days"
        className="h-11 border-2 border-black bg-white w-40"
      />
    </Field>

    <Field label="Cap redemptions per day or month?">
      <YesNo
        value={form.wants_redemption_cap}
        onChange={(v) => update("wants_redemption_cap", v)}
        testid="gs-wants-cap"
      />
      {form.wants_redemption_cap === true && (
        <Input
          value={form.redemption_cap_detail}
          onChange={(e) => update("redemption_cap_detail", e.target.value)}
          placeholder="e.g. 20/day or 500/month"
          data-testid="gs-cap-detail"
          className="mt-2 h-11 border-2 border-black bg-white"
        />
      )}
    </Field>

    <Field
      label="Vary rewards by day of the week?"
      hint="e.g. weekday spins = smaller reward, weekend = bigger."
    >
      <YesNo
        value={form.wants_day_of_week_variation}
        onChange={(v) => update("wants_day_of_week_variation", v)}
        testid="gs-wants-dow"
      />
    </Field>

    <Field
      label="Different rewards for new vs returning customers?"
      hint="Great for balancing acquisition with loyalty."
    >
      <YesNo
        value={form.wants_new_vs_returning_split}
        onChange={(v) => update("wants_new_vs_returning_split", v)}
        testid="gs-wants-nvr"
      />
    </Field>
  </div>
);

/* ─── Step 5 — Redemption ─────────────────────────────────────────── */

const StepRedemption = ({ form, update }) => (
  <div className="space-y-5">
    <SectionIntro
      title="How will you validate coupons?"
      body="Every winning play creates a code — here's how your store will verify it."
    />

    <Field label="Validation method">
      <RadioGroup
        value={form.validation_method}
        onValueChange={(v) => update("validation_method", v)}
        className="space-y-2"
      >
        {VALIDATION_METHOD_OPTIONS.map((opt) => (
          <label
            key={opt.value}
            htmlFor={`vm-${opt.value}`}
            className={`flex items-start gap-3 px-3 py-2.5 rounded-md border-2 cursor-pointer transition-transform hover:-translate-y-0.5 ${
              form.validation_method === opt.value
                ? "border-black bg-[var(--graffiti-yellow)] shadow-[3px_3px_0_black]"
                : "border-black bg-white"
            }`}
          >
            <RadioGroupItem
              id={`vm-${opt.value}`}
              value={opt.value}
              data-testid={`gs-validation-${opt.value}`}
              className="mt-0.5 border-black"
            />
            <span className="text-sm font-bold text-black leading-snug">
              {opt.label}
            </span>
          </label>
        ))}
      </RadioGroup>
    </Field>

    <Field
      label="Send SMS / WhatsApp confirmation to customers?"
      hint="Optional add-on — WhatsApp delivery pricing shown before you enable it."
    >
      <YesNo
        value={form.wants_sms_whatsapp_confirmation}
        onChange={(v) => update("wants_sms_whatsapp_confirmation", v)}
        testid="gs-wants-sms"
      />
    </Field>
  </div>
);

/* ─── Step 6 — Create account (mirrors VendorRegister) ────────────── */

const StepAccount = ({ form, update }) => (
  <div className="space-y-5">
    <div
      className="rounded-md border-2 border-black bg-[var(--graffiti-yellow)] px-4 py-3 flex items-center gap-3 shadow-[3px_3px_0_black]"
      data-testid="gs-trial-banner"
    >
      <Sparkles className="h-5 w-5 shrink-0" />
      <p className="text-sm font-bold text-black leading-snug">
        Complete this to activate your <u>15-day free trial</u> instantly. No
        credit card required.
      </p>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <Field label="Your full name" required>
        <Input
          value={form.name}
          onChange={(e) => update("name", e.target.value)}
          data-testid="gs-name"
          className="h-11 border-2 border-black bg-white"
        />
      </Field>
      <Field label="Business name" required>
        <Input
          value={form.business}
          onChange={(e) => update("business", e.target.value)}
          data-testid="gs-business"
          className="h-11 border-2 border-black bg-white"
        />
      </Field>
    </div>

    <Field
      label="Business category (from Step 1)"
      hint="Pre-filled from your Step 1 answer — you can still change it here."
    >
      <Select
        value={form.business_category}
        onValueChange={(v) => update("business_category", v)}
      >
        <SelectTrigger
          data-testid="gs-business-category-final"
          className="h-11 border-2 border-black bg-white"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {BUSINESS_TYPES.map((t) => (
            <SelectItem key={t} value={t}>{t}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </Field>

    {form.business_category === "Other" && (
      <Field label="Please specify your business category" required>
        <Input
          value={form.business_type_other}
          onChange={(e) => update("business_type_other", e.target.value)}
          placeholder="e.g. Pet supplies, Bookstore, Tiffin service"
          maxLength={80}
          data-testid="gs-business-type-other"
          className="h-11 border-2 border-black bg-white"
        />
      </Field>
    )}

    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <Field label="City" required>
        <Input
          value={form.city}
          onChange={(e) => update("city", e.target.value)}
          placeholder="e.g. Mumbai"
          data-testid="gs-city"
          className="h-11 border-2 border-black bg-white"
        />
      </Field>
      <Field label="Phone (+91)" required>
        <Input
          value={form.phone}
          onChange={(e) => update("phone", e.target.value)}
          placeholder="+91 9XXXXXXXXX"
          data-testid="gs-phone"
          className="h-11 border-2 border-black bg-white"
        />
      </Field>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <Field label="Email" required>
        <Input
          type="email"
          value={form.email}
          onChange={(e) => update("email", e.target.value)}
          data-testid="gs-email"
          className="h-11 border-2 border-black bg-white"
        />
      </Field>
      <div className="space-y-1.5">
        <Label className="uppercase text-[11px] font-bold tracking-wider text-black">
          Password <span className="text-[#8b1212] ml-1">*</span>
        </Label>
        <PasswordInput
          value={form.password}
          onChange={(e) => update("password", e.target.value)}
          data-testid="gs-password"
          className="h-11 border-2 border-black bg-white"
        />
        <p
          className={`text-xs font-bold ${
            form.password.length >= 8 ? "text-[#176b3a]" : "text-black/55"
          }`}
        >
          {form.password.length >= 8 ? "✓ Strong enough" : "Min 8 characters"}
        </p>
      </div>
    </div>

    <Field label="GST number (optional)">
      <Input
        value={form.gst_number}
        onChange={(e) => update("gst_number", e.target.value)}
        data-testid="gs-gst"
        className="h-11 border-2 border-black bg-white"
      />
    </Field>

    <label className="flex items-start gap-2 text-sm text-black/80 pt-1">
      <Checkbox
        checked={form.agree}
        onCheckedChange={(v) => update("agree", !!v)}
        data-testid="gs-agree"
        className="border-2 border-black"
      />
      <span>
        I agree to CityDooh's Terms &amp; Conditions and Privacy Policy.
      </span>
    </label>
  </div>
);

/* ─── Section intro block ────────────────────────────────────────── */

const SectionIntro = ({ title, body }) => (
  <div className="pb-1">
    <h2 className="font-display uppercase text-2xl leading-none">
      {title}
    </h2>
    <p className="text-sm text-black/70 mt-2 leading-snug">{body}</p>
  </div>
);
